import { Menu, MenuItem, Divider, ListItemIcon, Box, Paper, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GrayButton, TransparentButton } from '../../../components/StyledButton';
import { useLoading } from '../../../context/LoadingContext';
import { useAuth } from '../../../context/AuthContext';
import { format } from 'date-fns';
import { updateNotification } from '../../../axios/Notification/NotificationApi';
import { toast } from 'react-toastify';
import React, { useMemo, useState } from 'react';
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotificationDialog, {
   isApproverNotificationRole,
   isLeaveNotificationType,
   getLeaveNotificationRequestTitle,
   getLeaveNotificationResultTitle,
   getLeaveNotificationResultMessage,
   getLeaveNotificationPendingContent,
} from './NotificationDialog';
import IconNotificationBellBig from '../../../assets/images/icons/IconNotificationBellBig';
import { formatInTimeZone } from "date-fns-tz";
import { useNavigate } from "react-router-dom";
import { CustomTabs, CustomTabsV2 } from '../../../components/StyledComponents';
import Cookies from "js-cookie";

export function formatDateWithOffset(dateString, dateTimeFormat, i18n) {
   // 1. Format theo timezone máy
   const localFormatted = formatInTimeZone(
      dateString,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      dateTimeFormat
   );

   // 2. Lấy offset từ date backend (vd: +0700)
   const rawOffset = new Date(dateString)
      .toString()
      .match(/GMT([+-]\d{4})/)[1];

   const offsetFormatted = rawOffset.slice(0, 3) + ":" + rawOffset.slice(3);

   // 3. Chọn prefix theo ngôn ngữ
   const prefix = i18n.language.startsWith("vi") ? "GMT" : "UTC";

   return `${localFormatted} (${prefix} ${offsetFormatted})`;
}

const NotificationFirstDialog = ({
   onOpenFullDialog,
   notifications,
   handleRefreshData,
   allUser,
   onClose }) => {
   const { t, i18n } = useTranslation();
   const [show, hide] = useLoading();
   const { dateTimeFormat, isAdmin, profile } = useAuth();
   const [openNotificationFull, setOpenNotificationFull] = useState(false);
   const [tabValue, setTabValue] = useState(() => {
      const savedTab = Cookies.get("notification_tab");
      return savedTab ? Number(savedTab) : 0;
   });
   const [selectedId, setSelectedId] = useState(null);
   const navigate = useNavigate();

   // const refreshData = () => {
   //    handleRefreshData();
   // };

   // const handleCloseDialog = () => {
   //    onClose();
   // };

   const sortedNotifications = useMemo(() => {
      return notifications
         .filter((n) => {
            if (isAdmin) return true;

            if (n.type === "SWAP_SHIFT") {
               const fromId = n.detail?.fromUserId;
               const toId = n.detail?.toUserId;

               switch (n.status) {
                  case "PARTNER_PENDING":
                     return profile.userId === toId;

                  case "PENDING":
                     return profile.userId === fromId || profile.userId === n.user_id;

                  case "PARTNER_REJECTED":
                     return profile.userId === fromId;

                  case "APPROVED":
                  case "REJECTED":
                     return profile.userId === fromId || profile.userId === toId;

                  default:
                     return false;
               }
            }

            // các loại notification khác
            return n.user_id === profile.userId;
         })
         .sort((a, b) => {
            return new Date(b.dateTimeSendRequest) - new Date(a.dateTimeSendRequest);
         });
   }, [notifications, isAdmin, profile.userId]);

   const unreadNotifications = useMemo(() => {
      return sortedNotifications.filter((n) => n.unread);
   }, [sortedNotifications]);

   const handleChangeTab = (event, newValue) => {
      setTabValue(newValue);

      // lưu cookie 30 ngày
      Cookies.set("notification_tab", newValue, {
         expires: 30,
      });
   };

   const a11yProps = (index) => {
      return {
         id: `notification-tab-${index}`,
         'aria-controls': `notification-tabpanel-${index}`,
      };
   };

   const handleUpdateNotificationToRam = async (payload) => {
      try {
         const role = isAdmin ? "ADMIN" : "USER";
         const response = await updateNotification(payload, profile.userId, role, profile.companyId);
      } catch (error) {
         toast.error("Notifications updated in RAM fail");
      }
   };

   const handleNavigateByNotification = (notif) => {
      const { id, type, detail, requestId, eventId, eventVisitorId } = notif;

      if (type === "ADD_LEAVE" || type === "EDIT_LEAVE" || type === "DELETE_LEAVE") {
         navigate(`/controlpanel/leave-and-holiday-management?leaveId=${id}`);
         return;
      }

      if (type === "EVENTLOG") {
         navigate(`/controlpanel/event-log-management?requestId=${id}`);
         return;
      }

      if (type === "SWAP_SHIFT") {
         navigate(`/controlpanel/shift-and-roster-management?requestShiftId=${id}&requestId=${requestId}`);
         return;
      }

      if (type === "VISITOR") {
         navigate(`/controlpanel/visitor-management?eventId=${eventId}&eventVisitorId=${eventVisitorId}`);
         return;
      }
   };

   const handleRemoveNotification = (id) => {
      const updated = notifications.filter((item) => item.notificationId !== id);
      handleUpdateNotificationToRam(updated);
      handleRefreshData();
   };

   const handleReadAll = () => {
      const updatedNotifications = notifications.map((n) => ({
         ...n,
         unread: false,
      }));
      handleUpdateNotificationToRam(updatedNotifications);
      handleRefreshData();
      onClose();
   };

   const handleReadNotification = (id) => {
      const updated = notifications.map((n) =>
         n.notificationId === id ? { ...n, unread: false } : n
      );
      handleUpdateNotificationToRam(updated);
      handleRefreshData();
   };

   const handleClearAll = () => {
      handleUpdateNotificationToRam([]);
      handleRefreshData();
      onClose();
   };

   const handleCloseNotificationFullDialog = () => {
      setOpenNotificationFull(false);
   };

   function TabPanel(props) {
      const { children, value, index, ...other } = props;
      return (
         <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <div>{children}</div>}
         </div>
      );
   }

   function NotificationItem({
      date_request_event,
      end_date_leave,
      notificationId,
      request_type_id,
      processed_by_userId,
      start_date_leave,
      status,
      type,
      user_id,
      unread,
      dateTimeSendRequest,
      selectedId,
      setSelectedId,
      id,
      onNavigate,
      onClose,
      detail,
      role,
      requestId,
      isAdmin,
      request_user_id,
      eventId,
      eventName,
      eventVisitorId,
      visitorName
   }) {
      const userName =
         allUser.find((u) => u.userId === user_id)?.fullName || "";
      const requestUserName =
         allUser.find((u) => u.userId === request_user_id)?.fullName || "";
      const processed_name =
         allUser.find((u) => u.userId === processed_by_userId)?.fullName || "";
      const [anchorElNotification, setAnchorElNotification] = useState(null);
      const isOwner = user_id === profile.userId;
      const isPending =
         type === "SWAP_SHIFT"
            ? isApproverNotificationRole(role)
            : status === "REQUESTED";

      const isResultView =
         type === "SWAP_SHIFT"
            ? (role === "OWNER" && status === "PENDING") ||
            ["APPROVED", "REJECTED", "PARTNER_REJECTED"].includes(status)
            : isApproverNotificationRole(role)
               ? ["APPROVED", "REJECTED"].includes(status)
               : ["APPROVED", "REJECTED"].includes(status);

      const handleRowClick = () => {
         // chọn dòng
         setSelectedId(notificationId);
         // nếu chưa đọc thì mark đọc luôn
         if (unread) {
            handleReadNotification(notificationId);
         }

         const shouldNavigate = !(
            type === "SWAP_SHIFT" &&
            status === "PENDING" &&
            !isApproverNotificationRole(role)
         );

         if (shouldNavigate && onNavigate) {
            onNavigate({
               id,
               type,
               requestId,
               date_request_event,
               dateTimeSendRequest,
               eventId,
               eventVisitorId
            });
         }

         if (onClose) {
            onClose();
         }
      };

      return (
         // <div
         //    // onClick={() => setSelectedId(notificationId)}
         //    className={`flex items-start gap-3 py-3 px-2 cursor-pointer hover:bg-gray-50`}
         // // className={`flex items-start gap-3 py-3 px-2 hover:bg-gray-50 ${selectedId === notificationId ? "bg-[#EFF6FF]" : ""}`}
         // >
         <div
            onClick={handleRowClick}
            className={`flex items-start gap-3 py-3 px-4 cursor-pointer hover:bg-gray-50 ${selectedId === notificationId ? "bg-[#EFF6FF]" : ""
               }`}
         >

            {/* <div className='px-2'> */}
            {unread ? (
               <span className="w-2 h-2 bg-blue-500 rounded-full mt-[5px]"></span>
            ) : (
               <span className="w-2 h-2 invisible"></span>
            )}
            <div className="flex-1">
               {isResultView ? (
                  <>
                     {/* ===== RESULT ===== */}
                     <h4 className="text-sm text-neutral-900 font-semibold">
                        {type === "SWAP_SHIFT"
                           ? t("common.swap-shift-result")
                           : type === "EVENTLOG"
                              ? t("common.event-request-result")
                              : isLeaveNotificationType(type)
                                 ? getLeaveNotificationResultTitle(type, t)
                                 : t("common.attendance-visitor-request")}
                     </h4>

                     <p className="text-xs text-neutral-900 font-normal">
                        {type === "SWAP_SHIFT"
                           ? status === "PENDING" && role === "OWNER"
                              ? `${t("common.your-request-has-been-approved-by")} ${processed_name}`

                              : status === "APPROVED"
                                 ? `${t("common.your-request-has-been-approved-by")} ${processed_name}`

                                 : ["REJECTED", "PARTNER_REJECTED"].includes(status)
                                    ? `${t("common.your-request-has-been-rejected-by")} ${processed_name}`
                                    : ""

                           : type === "EVENTLOG"
                              ? status === "APPROVED"
                                 ? request_type_id === 1
                                    ? `${t("common.your-request-to-add-event-has-been-appove-by")} ${processed_name}`
                                    : `${t("common.your-request-to-edit-event-has-been-appove-by")} ${processed_name}`
                                 : request_type_id === 1
                                    ? `${t("common.your-request-to-add-event-has-been-rejected-by")} ${processed_name}`
                                    : `${t("common.your-request-to-edit-event-has-been-rejected-by")} ${processed_name}`

                              : isLeaveNotificationType(type)
                                 ? getLeaveNotificationResultMessage(type, status, processed_name, t)
                                 : ""}
                     </p>
                  </>
               ) : (
                  <>
                     {/* ===== REQUEST (PENDING) ===== */}
                     <h4 className="text-sm text-neutral-900 font-semibold">
                        {type === "SWAP_SHIFT"
                           ? t("common.request-swap")
                           : type === "EVENTLOG"
                              ? request_type_id === 1
                                 ? t("common.attendance-event-add-request")
                                 : t("common.request-to-edit-an-attendance-event")
                              : isLeaveNotificationType(type)
                                 ? getLeaveNotificationRequestTitle(type, t)
                                 : t("common.attendance-visitor-request")}
                     </h4>

                     <p className="text-xs text-neutral-900 font-normal">
                        {type === "SWAP_SHIFT" && status === "PARTNER_PENDING"
                           ? `${detail?.fullNameFrom} ${t("common.request-swap-with-you")}`

                           : type === "SWAP_SHIFT" && isApproverNotificationRole(role)
                              ? t("common.request-swap-by", {
                                 fromName: detail?.fullNameFrom,
                                 toName: detail?.fullNameTo
                              })

                              : type === "VISITOR"
                                 ? `${visitorName} ${t("common.visitor-request-des")} ${eventName}`

                                 : type === "EVENTLOG"
                                    ? request_type_id === 1
                                       ? `${requestUserName} ${t("common.content-request-notification-event-check-in")}`
                                       : `${requestUserName} ${t("common.content-request-notification-event-check-out")}`

                                    : isLeaveNotificationType(type)
                                       ? getLeaveNotificationPendingContent(type, requestUserName, t)
                                       : ""}
                     </p>
                  </>
               )}

               <p className="text-10 text-gray-500 font-normal mt-[4px]">
                  {formatDateWithOffset(dateTimeSendRequest, dateTimeFormat, i18n)}
               </p>
            </div>

            <button
               className="p-1 text-gray-400 hover:text-gray-600"
               onClick={(e) => {
                  e.stopPropagation();
                  setAnchorElNotification(e.currentTarget);
               }}
            >
               <MoreVertIcon fontSize="small" />
            </button>


            <Menu
               anchorEl={anchorElNotification}
               open={Boolean(anchorElNotification)}
               onClose={() => setAnchorElNotification(null)}
               anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
               }}
               transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
               }}
               PaperProps={{
                  sx: {
                     border: "1px solid #e5e7eb",
                     borderRadius: "8px",
                     boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                  },
               }}
            >
               {unread && (
                  <>
                     <MenuItem
                        onClick={(e) => {
                           e.stopPropagation();          // ⬅️ QUAN TRỌNG
                           handleReadNotification(notificationId);
                           setAnchorElNotification(null);
                        }}
                     >
                        <ListItemIcon>
                           <DoneAllIcon fontSize="small" />
                        </ListItemIcon>
                        {t("common.read")}
                     </MenuItem>
                     <Divider sx={{ mx: 2 }} />
                  </>
               )}

               <MenuItem
                  onClick={(e) => {
                     e.stopPropagation();          // ⬅️ QUAN TRỌNG
                     handleRemoveNotification(notificationId);
                     setAnchorElNotification(null);
                  }}
                  sx={{ color: "red" }}
               >
                  <ListItemIcon>
                     <DeleteIcon fontSize="small" sx={{ color: "red" }} />
                  </ListItemIcon>
                  {t("common.clear")}
               </MenuItem>

            </Menu>
            {/* </div> */}
         </div>

      );
   }

   return (
      <Paper
         elevation={0}
         sx={{
            width: 380,
            maxHeight: 500,
            overflow: "hidden",
            borderRadius: 0,
            boxShadow: "none",
         }}
      >
         <Box className="flex justify-between items-center px-4 py-2">
            <div className="flex items-center gap-2">
               <p className='text-xl font-semibold '>{t('common.notifications')}</p>
               {unreadNotifications.length > 0 && (
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#F3F4F6] text-sm">
                     {unreadNotifications.length}
                  </div>
               )}
            </div>
            <TransparentButton
               onClick={handleReadAll}
               className={`transition ${unreadNotifications.length === 0 ? "opacity-50 pointer-events-none" : ""
                  }`}
            >
               <span className="text-sm text-blue-500">{t("common.read-all")}</span>
            </TransparentButton>
         </Box>
         <div className='h-[1px] bg-gray-200'></div>

         <Box className="">
            <CustomTabsV2
               variant="fullWidth"
               indicatorColor="none"
               value={tabValue}
               onChange={handleChangeTab}
            >
               <Tab
                  label={<p className="text-sm">{t("common.all")}</p>}
                  {...a11yProps(0)}
                  disableRipple
               />

               <Tab
                  label={<p className="text-sm">{t("common.unread")}</p>}
                  {...a11yProps(1)}
                  disableRipple
               />
            </CustomTabsV2>
         </Box>

         <Box className="h-[300px] flex flex-col">
            {sortedNotifications.length === 0 ? (
               <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                  <IconNotificationBellBig className="mb-2 w-8 h-8" />
                  <p className="text-sm font-semibold text-gray-800 mt-4">
                     {t("common.no-notifications")}
                  </p>
               </div>
            ) : (
               <div className="flex-1 overflow-y-auto">
                  {/* TAB ALL */}
                  <TabPanel value={tabValue} index={0}>
                     {sortedNotifications.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-500 h-[240px]">
                           <IconNotificationBellBig className="mb-2 w-8 h-8" />
                           <p className="text-sm font-semibold text-gray-800 mt-4">
                              {t("common.no-notifications")}
                           </p>
                        </div>
                     ) : (
                        sortedNotifications.map((n) => (
                           <React.Fragment key={n.notificationId}>
                              <NotificationItem
                                 {...n}
                                 selectedId={selectedId}
                                 setSelectedId={setSelectedId}
                                 onNavigate={handleNavigateByNotification}
                                 onClose={onClose}
                                 isAdmin={isAdmin}
                              />
                              <div className="h-[1px] bg-gray-200"></div>
                           </React.Fragment>
                        ))
                     )}
                  </TabPanel>

                  {/* TAB UNREAD */}
                  <TabPanel value={tabValue} index={1}>
                     {unreadNotifications.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-500 h-[240px]">
                           <IconNotificationBellBig className="mb-2 w-8 h-8" />
                           <p className="text-sm font-semibold text-gray-800 mt-4">
                              {t("common.no-notifications")}
                           </p>
                        </div>
                     ) : (
                        unreadNotifications.map((n) => (
                           <React.Fragment key={n.notificationId}>
                              <NotificationItem
                                 {...n}
                                 selectedId={selectedId}
                                 setSelectedId={setSelectedId}
                                 onNavigate={handleNavigateByNotification}
                                 onClose={onClose}
                                 isAdmin={isAdmin}
                              />
                              <div className="h-[1px] bg-gray-200 mx-7"></div>
                           </React.Fragment>
                        ))
                     )}
                  </TabPanel>
               </div>
            )}
         </Box>
         <Box className="bg-gray-100 px-4 py-2 flex justify-between items-center border-t border-gray-200">
            <GrayButton onClick={onOpenFullDialog} >
               <span className="text-sm text-gray-600 font-medium">
                  {t("common.show-all-arlerts")}
               </span>
            </GrayButton>
            <GrayButton onClick={handleClearAll} className={`transition ${sortedNotifications.length === 0 ? "opacity-50 pointer-events-none" : ""
               }`}>
               <span className="text-sm text-red-600 font-medium">
                  {t("common.button-clear-all")}
               </span>
            </GrayButton>
         </Box>

         {openNotificationFull && (
            <NotificationDialog
               onClose={handleCloseNotificationFullDialog}
               notifications={sortedNotifications}
               handleRefreshData={handleRefreshData}
               allUser={allUser}
            />
         )}
      </Paper>
   );
};

export default NotificationFirstDialog;
